import type { LoaderFunctionArgs, MetaFunction } from 'react-router'
import { useLoaderData, useSearchParams, Link } from 'react-router'
import { db } from '@afficheservices/db'
import {
  professionalProfiles,
  professionalCategories,
  serviceCategories,
} from '@afficheservices/db/schema'
import { eq, and, sql, isNotNull, isNull } from 'drizzle-orm'
import { geoFrance } from '@afficheservices/db/schema'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { ProMap } from '~/components/pro-map'
import { BreadcrumbSchema } from '~/components/structured-data'
import { STATIC_PAGE_HEADERS } from '~/lib/cache.server'
import { Map as MapIcon, Filter, MapPin } from 'lucide-react'
import { VoirAussi, CROSS_LINKS } from '~/components/voir-aussi'

export function headers() { return STATIC_PAGE_HEADERS }

export const meta: MetaFunction = () => [
  { title: "Carte des artisans en France — AfficheServices" },
  { name: 'description', content: "Carte interactive des artisans vérifiés partout en France. Filtrez par métier et trouvez un professionnel près de chez vous." },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/carte-artisans' },
  { property: 'og:title', content: 'Carte des artisans en France — AfficheServices' },
  { property: 'og:description', content: 'Carte interactive des artisans vérifiés partout en France.' },
  { property: 'og:url', content: 'https://www.afficheservices.fr/carte-artisans' },
]

function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLng = ((lng2 - lng1) * Math.PI) / 180
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(a))
}

export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url)
  const categorySlug = url.searchParams.get('categorie') ?? ''
  const postalRaw = (url.searchParams.get('postal') ?? '').trim()
  const postal = /^\d{5}$/.test(postalRaw) ? postalRaw : ''
  const radiusKm = Math.min(200, Math.max(5, parseInt(url.searchParams.get('radius') ?? '30', 10) || 30))

  // Load all active parent categories for the filter dropdown
  const categories = await db
    .select({
      id: serviceCategories.id,
      slug: serviceCategories.slug,
      name: serviceCategories.nameFr,
    })
    .from(serviceCategories)
    .where(and(
      isNull(serviceCategories.parentId),
      eq(serviceCategories.isActive, true),
    ))
    .orderBy(serviceCategories.displayOrder)

  // Resolve the active category (if filter applied)
  const activeCategory = categorySlug
    ? categories.find((c) => c.slug === categorySlug)
    : undefined

  // Query pros with coordinates. Cap at 500 markers to keep the bundle
  // and the map responsive. The split-view on /recherche handles deep
  // exploration; this page is a macro overview.
  const whereParts = [
    isNotNull(professionalProfiles.latitude),
    isNotNull(professionalProfiles.longitude),
  ]

  let pros
  if (activeCategory) {
    pros = await db
      .select({
        id: professionalProfiles.id,
        companyName: professionalProfiles.companyName,
        slug: professionalProfiles.slug,
        city: professionalProfiles.city,
        ratingAvg: professionalProfiles.ratingAvg,
        reviewCount: professionalProfiles.reviewCount,
        identityVerified: professionalProfiles.identityVerified,
        latitude: professionalProfiles.latitude,
        longitude: professionalProfiles.longitude,
      })
      .from(professionalProfiles)
      .innerJoin(
        professionalCategories,
        eq(professionalCategories.professionalId, professionalProfiles.id),
      )
      .where(and(
        eq(professionalCategories.categoryId, activeCategory.id),
        ...whereParts,
      ))
      .limit(500)
  } else {
    pros = await db
      .select({
        id: professionalProfiles.id,
        companyName: professionalProfiles.companyName,
        slug: professionalProfiles.slug,
        city: professionalProfiles.city,
        ratingAvg: professionalProfiles.ratingAvg,
        reviewCount: professionalProfiles.reviewCount,
        identityVerified: professionalProfiles.identityVerified,
        latitude: professionalProfiles.latitude,
        longitude: professionalProfiles.longitude,
      })
      .from(professionalProfiles)
      .where(and(...whereParts))
      .limit(500)
  }

  // Total count for the "N artisans localisés" badge
  const [totalRow] = await db
    .select({ count: sql<number>`count(*)::int` })
    .from(professionalProfiles)
    .where(and(
      isNotNull(professionalProfiles.latitude),
      isNotNull(professionalProfiles.longitude),
    ))
  const totalLocated = Number(totalRow?.count ?? 0)

  // Count per category for the filter chips
  const catCounts = await db
    .select({
      categoryId: professionalCategories.categoryId,
      count: sql<number>`count(DISTINCT ${professionalCategories.professionalId})::int`,
    })
    .from(professionalCategories)
    .innerJoin(
      professionalProfiles,
      eq(professionalProfiles.id, professionalCategories.professionalId),
    )
    .where(and(
      isNotNull(professionalProfiles.latitude),
      isNotNull(professionalProfiles.longitude),
    ))
    .groupBy(professionalCategories.categoryId)

  const countByCategoryId = new Map<string, number>()
  for (const c of catCounts) countByCategoryId.set(c.categoryId, Number(c.count))

  // Location-based radius filter (postal code → coords → Haversine)
  let center: { lat: number; lng: number; label: string } | null = null
  if (postal) {
    const [geo] = await db
      .select({
        lat: geoFrance.latitude,
        lng: geoFrance.longitude,
        name: geoFrance.communeName,
      })
      .from(geoFrance)
      .where(eq(geoFrance.postalCode, postal))
      .limit(1)
    if (geo) {
      center = { lat: parseFloat(geo.lat), lng: parseFloat(geo.lng), label: `${geo.name} (${postal})` }
    }
  }

  const filteredPros = center
    ? pros.filter((p) => {
        if (!p.latitude || !p.longitude) return false
        const d = haversineKm(center!.lat, center!.lng, parseFloat(p.latitude), parseFloat(p.longitude))
        return d <= radiusKm
      })
    : pros

  return {
    pros: filteredPros,
    categories: categories.map((c) => ({
      ...c,
      count: countByCategoryId.get(c.id) ?? 0,
    })),
    activeSlug: categorySlug,
    activeCategory,
    totalLocated,
    center,
    radiusKm,
    postal,
  }
}

export default function CarteArtisansPage() {
  const { pros, categories, activeSlug, activeCategory, totalLocated, center, radiusKm, postal } = useLoaderData<typeof loader>()
  const [_searchParams] = useSearchParams()

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Carte des artisans', url: 'https://www.afficheservices.fr/carte-artisans' },
      ]} />

      <section className="border-b border-border bg-slate-50 px-4 py-8">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700">
                <MapIcon className="h-3 w-3" /> Vue carte
              </div>
              <h1 className="mt-3 text-2xl font-bold text-slate-900 sm:text-3xl">
                Carte des artisans en France
              </h1>
              <p className="mt-1 text-sm text-slate-600">
                {totalLocated.toLocaleString('fr-FR')} artisans vérifiés localisés.{' '}
                {activeCategory && (
                  <>
                    Filtré sur <strong className="text-slate-900">{activeCategory.name}</strong>.
                  </>
                )}
              </p>
            </div>
            <Link
              to="/devis-gratuit"
              className="rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-hover"
            >
              Demander un devis
            </Link>
          </div>

          {/* Location + radius search (GET form — SSR-friendly, shareable URL) */}
          <form method="get" className="mt-5 flex flex-wrap items-end gap-3 rounded-xl border border-border bg-white p-3 shadow-xs">
            {activeSlug && <input type="hidden" name="categorie" value={activeSlug} />}
            <label className="flex flex-col gap-1 text-xs">
              <span className="font-semibold text-slate-700">Code postal</span>
              <input
                type="text"
                name="postal"
                defaultValue={postal}
                inputMode="numeric"
                pattern="\d{5}"
                placeholder="06000"
                maxLength={5}
                className="w-28 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-primary focus:outline-none"
              />
            </label>
            <label className="flex flex-1 flex-col gap-1 text-xs">
              <span className="font-semibold text-slate-700">Rayon : {radiusKm} km</span>
              <input
                type="range"
                name="radius"
                min={5}
                max={200}
                step={5}
                defaultValue={radiusKm}
                className="accent-primary"
              />
            </label>
            <button
              type="submit"
              className="rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-white hover:bg-primary-hover"
            >
              Chercher
            </button>
            {center && (
              <Link to="/carte-artisans" className="text-xs text-slate-500 hover:text-primary underline">
                Réinitialiser
              </Link>
            )}
          </form>

          {center && (
            <p className="mt-3 text-sm text-slate-600">
              <MapPin className="mr-1 inline h-4 w-4 text-primary" />
              <strong>{pros.length}</strong> artisans dans un rayon de {radiusKm} km autour de <strong>{center.label}</strong>
            </p>
          )}

          {/* Category filter — links so it's indexable and shareable */}
          <div className="mt-6 flex items-start gap-2">
            <Filter className="mt-1 h-4 w-4 shrink-0 text-slate-400" />
            <div className="flex flex-wrap gap-2">
              <Link
                to="/carte-artisans"
                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                  !activeSlug
                    ? 'border-primary bg-primary text-white'
                    : 'border-border bg-white text-slate-600 hover:border-primary hover:text-primary'
                }`}
              >
                Tous ({totalLocated.toLocaleString('fr-FR')})
              </Link>
              {categories
                .filter((c) => c.count > 0)
                .sort((a, b) => b.count - a.count)
                .slice(0, 15)
                .map((c) => (
                  <Link
                    key={c.id}
                    to={`/carte-artisans?categorie=${c.slug}`}
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                      activeSlug === c.slug
                        ? 'border-primary bg-primary text-white'
                        : 'border-border bg-white text-slate-600 hover:border-primary hover:text-primary'
                    }`}
                  >
                    {c.name} ({c.count.toLocaleString('fr-FR')})
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 py-6 lg:px-6">
        {pros.length === 0 ? (
          <div className="mt-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-10 text-center">
            <MapPin className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-3 text-sm font-semibold text-slate-600">Aucun artisan localisé pour ce filtre</p>
            <p className="mt-1 text-xs text-slate-400">Essayez une autre catégorie ou retirez le filtre.</p>
          </div>
        ) : (
          <ProMap
            pros={pros}
            centerLat={center?.lat}
            centerLng={center?.lng}
            className="h-[600px] rounded-2xl border border-border shadow-xs"
          />
        )}

        {/* Text block for SEO */}
        <section className="mt-10 max-w-3xl text-sm leading-relaxed text-slate-700">
          <h2 className="text-xl font-bold text-slate-900">Trouvez un artisan près de chez vous</h2>
          <p className="mt-3">
            Cette carte interactive regroupe tous les artisans référencés par AfficheServices dont les
            coordonnées géographiques sont disponibles via les données SIRENE officielles. Chaque marqueur
            correspond à une entreprise vérifiée (SIRET validé, adresse officielle).
          </p>
          <p className="mt-3">
            Utilisez les filtres pour restreindre l'affichage à un métier spécifique : plombier, électricien,
            menuisier, couvreur, maçon, peintre, et plus. Pour une recherche plus précise avec avis et
            disponibilité, utilisez notre <Link to="/recherche" className="font-semibold text-primary hover:underline">recherche avancée</Link>.
          </p>
          <p className="mt-3">
            Pour recevoir plusieurs devis comparatifs d'artisans certifiés RGE, privilégiez notre
            formulaire de <Link to="/devis-gratuit" className="font-semibold text-primary hover:underline">demande de devis gratuit</Link>
            {' '}(réponse sous 48h).
          </p>
        </section>

        <VoirAussi links={CROSS_LINKS.geo} />
      </div>

      <Footer />
    </div>
  )
}
